/**
 * ECharts Option Builder — Bar / Histogram chart.
 */
import type { EChartsOption } from 'echarts';
import { type ChartTheme, buildBaseOption, legendConfig, xAxisConfig, yAxisConfig } from '../theme';
import { formatChartValue } from '../../../../utils/formatters';
import type { ChartData } from '../../../../api/types';
import { autoRotate, estimateYAxisNameGap } from './utils';
import { extractSeriesData } from './seriesData';

export function buildBarChart(
  data: ChartData,
  theme: ChartTheme,
  animated: boolean,
  histogram: boolean,
  containerWidth: number = 400,
): EChartsOption {
  const sd = extractSeriesData(data);

  // Stacking is applied later by applyChartStyle() — base builder
  // uses sensible non-stacked defaults.
  // barWidth: histograms use fixed 92%; regular bars omit barWidth so
  // ECharts auto-sizes based on category count. User can override via
  // the visual config slider.
  const borderRadius = [0, 0, 0, 0];

  const series = sd.seriesNames.map((name) => ({
    type: 'bar' as const,
    name,
    data: sd.seriesValues.get(name)!,
    ...(histogram ? { barWidth: '92%' } : {}),
    itemStyle: { borderRadius },
    emphasis: { focus: 'none' as const, scale: false },
  }));

  // Show y-axis label when single series (label already contains friendly name)
  const yAxisName = sd.seriesNames.length === 1 ? sd.seriesNames[0] : undefined;
  const nameGap = estimateYAxisNameGap(sd.seriesValues);

  // X-axis name: use the x column name so it appears when axis label is enabled
  const meta = data.meta;
  const xCol = meta?.x ?? data.columns[0].name;

  return {
    ...buildBaseOption(theme, animated),
    legend: { ...legendConfig(theme), show: sd.seriesNames.length > 1 },
    xAxis: {
      ...xAxisConfig(theme),
      name: xCol,
      data: sd.categories,
      axisLabel: { ...xAxisConfig(theme).axisLabel, rotate: autoRotate(sd.categories, containerWidth) },
    },
    yAxis: {
      ...yAxisConfig(theme),
      name: yAxisName,
      nameGap,
      axisLabel: { ...yAxisConfig(theme).axisLabel, formatter: (v: number) => formatChartValue(v) },
    },
    series,
  };
}